import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { parseISO, addMonths } from 'date-fns';
import './GsecSellDealModal.css';
import { API_BASE_URL } from '../services/api';

// Helper to truncate to 4 decimals
function truncate4(val) {
  return Math.floor(Number(val) * 10000) / 10000;
}

// Helper to safely parse ISO date strings
function safeParseISO(val) {
  if (!val) return null;
  if (typeof val === 'string') return parseISO(val);
  if (val instanceof Date) return val;
  return null;
}

// Calculate days difference between two dates (matching frontend exactly)
// Frontend uses: Math.round(Math.abs((date1 - date2) / millisecondsPerDay))
function getDaysDifference(date1, date2) {
  if (!date1 || !date2) return 0;
  const d1 = safeParseISO(date1);
  const d2 = safeParseISO(date2);
  if (!d1 || !d2) return 0;
  // Match frontend calculation exactly: Math.round(Math.abs((date1 - date2) / millisecondsPerDay))
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return Math.round(Math.abs((d1 - d2) / millisecondsPerDay));
}

// Generate full coupon schedule
function generateFullCouponSchedule(issueDate, maturityDate, couponDate1, couponDate2) {
  const schedule = [];
  const issue = safeParseISO(issueDate);
  const maturity = safeParseISO(maturityDate);
  
  if (!issue || !maturity) return schedule;
  
  // Parse coupon dates
  const c1 = safeParseISO(couponDate1);
  const c2 = safeParseISO(couponDate2);
  
  if (!c1 || !c2) return schedule;
  
  // Generate schedule from issue to maturity
  let currentDate = new Date(issue);
  const endDate = new Date(maturity);
  
  // Add issue date
  schedule.push(new Date(issue));
  
  // Generate coupon dates
  while (currentDate < endDate) {
    // Add 6 months for semi-annual coupons
    currentDate = addMonths(currentDate, 6);
    if (currentDate <= endDate) {
      schedule.push(new Date(currentDate));
    }
  }
  
  // Add maturity date
  schedule.push(new Date(maturity));
  
  return schedule;
}

// Calculate NVP (clean price) using system date as value date (matching frontend logic exactly)
function calculateNVP({
  faceValue: originalFaceValue, // Not used directly in per-100 calcs
  couponRate,
  yieldRate,
  systemDate,
  maturityDate,
  issueDate,
  couponDate1,
  couponDate2
}) {
  if (!originalFaceValue || !couponRate || !yieldRate || !systemDate || !maturityDate || !issueDate || !couponDate1 || !couponDate2) {
    return { nvp: '', accruedInterest: '' };
  }

  // Fixed semi-annual frequency
  const frequency = 2;
  const monthsPerPeriod = 12 / frequency; // 6 months for semi-annual

  // Parse and normalize all date inputs
  const issue = safeParseISO(issueDate);
  const maturity = safeParseISO(maturityDate);
  const settle = safeParseISO(systemDate);
  const c1 = safeParseISO(couponDate1);
  const c2 = safeParseISO(couponDate2);

  if (!issue || !maturity || !settle || !c1 || !c2) {
    return { nvp: '', accruedInterest: '' };
  }

  // Use per-100 basis from the start (matching frontend calculation exactly)
  const fv = 100; // Always use 100 as face value for per-100 calculations
  const cr = Number(couponRate) / 100; // Convert percentage to decimal
  const ytm = Number(yieldRate) / 100; // Convert percentage to decimal
  
  // Calculate coupon payment per 100 (matching frontend)
  const coupon = fv * cr / frequency;
  const ytmPerPeriod = ytm / frequency;

  // Find last and next coupon dates (matching frontend logic exactly)
  // Start from issue date and move forward until past settle date, then step back
  let lastCoupon = new Date(issue);
  
  // Move forward until we're past the settle date
  while (lastCoupon <= settle) {
    lastCoupon.setMonth(lastCoupon.getMonth() + monthsPerPeriod);
  }
  
  // Step back to get the last coupon date before settle date
  lastCoupon.setMonth(lastCoupon.getMonth() - monthsPerPeriod);
  
  // Create next coupon date by adding one period to last coupon
  const nextCoupon = new Date(lastCoupon);
  nextCoupon.setMonth(nextCoupon.getMonth() + monthsPerPeriod);

  const lastCouponDate = lastCoupon;
  const nextCouponDate = nextCoupon;

  // Calculate days for accrued interest (matching frontend)
  const daysAccrued = getDaysDifference(settle, lastCouponDate);
  const daysInCouponPeriod = getDaysDifference(nextCouponDate, lastCouponDate);
  
  if (daysInCouponPeriod === 0) {
    return { nvp: '', accruedInterest: '' };
  }

  // Calculate fractional period (matching frontend logic exactly)
  // Fractional period = days from settle date to next coupon / days in coupon period
  const daysToNextCoupon = getDaysDifference(nextCouponDate, settle);
  const fractionalPeriod = daysToNextCoupon / daysInCouponPeriod;

  // Generate cash flows from next coupon to maturity (matching frontend)
  const cashFlows = [];
  let cfDate = new Date(nextCouponDate);
  let periodCount = 0;

  while (cfDate <= maturity) {
    const isFinal = cfDate.getTime() === maturity.getTime();
    const amount = isFinal ? coupon + fv : coupon; // Final includes face value
    cashFlows.push({
      date: new Date(cfDate),
      amount,
      periodCount
    });
    cfDate.setMonth(cfDate.getMonth() + monthsPerPeriod);
    periodCount++;
  }

  // Calculate present value of each cash flow (matching frontend)
  let dirtyPrice = 0;
  for (const cf of cashFlows) {
    const t = fractionalPeriod + cf.periodCount;
    const pv = cf.amount / Math.pow(1 + ytmPerPeriod, t);
    dirtyPrice += pv;
  }

  // Truncate dirty price to 4 decimals (matching frontend)
  const dirtyPrice10000 = Math.floor(dirtyPrice * 10000 + 0.0001);
  const truncatedDirtyPrice = dirtyPrice10000 / 10000;

  // Calculate accrued interest per 100 (matching frontend logic)
  const accruedInterest = coupon * (daysAccrued / daysInCouponPeriod);
  const truncatedAccruedInterestPer100 = Math.floor(accruedInterest * 10000) / 10000;

  // Clean price is dirty price minus accrued interest (matching frontend)
  const cleanPrice = truncatedDirtyPrice - truncatedAccruedInterestPer100;
  const truncatedCleanPrice = Math.floor(cleanPrice * 10000) / 10000;

  return {
    nvp: truncatedCleanPrice.toFixed(4),
    accruedInterest: truncatedAccruedInterestPer100.toFixed(4)
  };
}

function GsecSellDealModal({
  show,
  onClose,
  onConfirm,
  onPortfolioChange,
  isin,
  gsecFaceValue,
  cleanPrice,
  isinData,
  formValueDate,
  strictFaceValueMatch = true
}) {
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState('');
  const [deals, setDeals] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState([]); // [{dealId, amountToSell}]
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAmountWarning, setShowAmountWarning] = useState(false);
  const [amountDifference, setAmountDifference] = useState(0);
  
  // New state for gain/loss calculation
  const [gainLossData, setGainLossData] = useState({
    nvp: '',
    cleanPrice: '',
    difference: '',
    isGain: false,
    percentage: ''
  });
  
  // State for GSEC report data and balance validation
  const [gsecReportData, setGsecReportData] = useState([]);
  const [balanceValidation, setBalanceValidation] = useState({
    isValid: true,
    message: '',
    validationResults: []
  });

  useEffect(() => {
    if (show) {
      axios.get(`${API_BASE_URL}/portfolios`).then(res => {
        setPortfolios(res.data);
      });
      
      // Fetch GSEC report data for balance validation
      const asAtDate = new Date().toISOString().split('T')[0];
      axios.get(`${API_BASE_URL}/reports/gsec`, { params: { asAtDate } }).then(res => {
        setGsecReportData(res.data?.data || []);
      }).catch(err => {
        console.error('Failed to fetch GSEC report data:', err);
        setGsecReportData([]);
      });
      
      setSelectedPortfolio('');
      setDeals([]);
      setFilteredDeals([]);
      setSelectedDeals([]);
      setSearch('');
      setError('');
      setShowAmountWarning(false);
      setAmountDifference(0);
      setBalanceValidation({
        isValid: true,
        message: '',
        validationResults: []
      });
    }
  }, [show]);

  useEffect(() => {
    if (selectedPortfolio) {
      setLoading(true);
      // Fetch buy deals with remaining face value for the selected portfolio and ISIN from backend
      axios.get(`${API_BASE_URL}/isin-master/gsec/buy-deals?isin=${isin || ''}&portfolio=${selectedPortfolio}`)
        .then(res => {
          const allDeals = (res.data && res.data.data) ? res.data.data : res.data;
          setDeals(allDeals);
          // Filter out deals whose value_date is after the form's value date
          const filteredByDate = Array.isArray(allDeals) && formValueDate
            ? allDeals.filter(d => {
                if (!d.value_date) return true;
                try {
                  return new Date(d.value_date) <= new Date(formValueDate);
                } catch (_) { return true; }
              })
            : allDeals;
          setFilteredDeals(filteredByDate);
          setSelectedDeals([]);
        })
        .catch(() => setError('Failed to fetch deals'))
        .finally(() => setLoading(false));
    }
  }, [selectedPortfolio, isin, formValueDate]);

  // Calculate gain/loss when clean price, isin data, or selected deals change
  useEffect(() => {
    console.log('Gain/Loss Calculation Debug:');
    console.log('cleanPrice (from form):', cleanPrice);
    console.log('formValueDate (sell value date):', formValueDate);
    console.log('isinData:', isinData);
    console.log('isinData keys:', isinData ? Object.keys(isinData) : 'No isinData');
    console.log('selectedDeals:', selectedDeals);
    
    // Use clean price from form - it should already be calculated for the sell value date
    // The cleanPrice represents the sell price per 100 that the user is selling at
    // This is calculated in the parent form using form.valueDate (sell value date) and form.yield (sell yield)
    let sellPricePer100 = cleanPrice ? parseFloat(cleanPrice) : null;
    
    if (sellPricePer100 !== null) {
      console.log('Sell Price per 100 (from form cleanPrice):', sellPricePer100);
      console.log('Sell value date:', formValueDate);
      console.log('Note: This cleanPrice is calculated using sell value date and sell yield from main form');
    }
    
    // Simple fallback calculation if we have clean price but no ISIN data
    if (sellPricePer100 !== null && !isinData) {
      console.log('Using simple fallback calculation - no ISIN data');
      const amountToUse = selectedDeals.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0);
      
      // Only calculate if deals are actually selected and have amounts
      if (amountToUse > 0 && selectedDeals.length > 0) {
        // Calculate NVP for each selected deal using their individual yields
        let totalNVP = 0;
        let totalSellValue = 0;
        let weightedNVPPer100 = 0;
        let dealCalculations = [];
        
        for (const selectedDeal of selectedDeals) {
          const deal = deals.find(d => d.id === selectedDeal.dealId);
          if (deal && selectedDeal.amountToSell && Number(selectedDeal.amountToSell) > 0) {
            // Use deal's yield for NVP calculation
            const dealYield = deal.yield || 12.0;
            const dealAmount = Number(selectedDeal.amountToSell);
            
            // Simple NVP estimation based on deal's yield
            const estimatedNVPPer100 = Math.max(85, 100 - (dealYield - 7) * 1.5);
            const sellPriceForCalculation = sellPricePer100 !== null ? sellPricePer100 : parseFloat(cleanPrice || 0);
            
            const dealNVP = (estimatedNVPPer100 / 100) * dealAmount;
            const dealSellValue = (sellPriceForCalculation / 100) * dealAmount;
            
            totalNVP += dealNVP;
            totalSellValue += dealSellValue;
            weightedNVPPer100 += estimatedNVPPer100 * (dealAmount / amountToUse);
            
            dealCalculations.push({
              dealNumber: deal.deal_number,
              yield: deal.yield,
              amount: dealAmount,
              nvpPer100: estimatedNVPPer100,
              dealNVP: dealNVP,
              dealSellValue: dealSellValue,
              gainLoss: dealSellValue - dealNVP
            });
            
            console.log(`Deal ${deal.deal_number}: Yield ${deal.yield}%, NVP per 100: ${estimatedNVPPer100}, Amount: ${dealAmount}`);
          }
        }
        
        if (totalNVP > 0) {
          const capitalGainLoss = totalSellValue - totalNVP;
          const percentage = totalNVP > 0 ? ((capitalGainLoss / totalNVP) * 100) : 0;
          
          setGainLossData({
            nvp: weightedNVPPer100.toFixed(4),
            cleanPrice: parseFloat(cleanPrice).toFixed(4),
            totalNVP: totalNVP.toFixed(2),
            totalSellValue: totalSellValue.toFixed(2),
            capitalGainLoss: capitalGainLoss.toFixed(2),
            isGain: capitalGainLoss > 0,
            percentage: percentage.toFixed(2),
            totalAmount: amountToUse.toFixed(2),
            dealCalculations: dealCalculations
          });
        }
      } else {
        // No deals selected, clear the data
        console.log('No deals selected or no amounts entered');
        setGainLossData({
          nvp: '',
          cleanPrice: '',
          totalNVP: '',
          totalSellValue: '',
          capitalGainLoss: '',
          isGain: false,
          percentage: '',
          totalAmount: '',
          dealCalculations: []
        });
      }
      return;
    }
    
    if (cleanPrice && isinData) {
      console.log('Checking ISIN data fields:');
      console.log('face_value:', isinData.face_value);
      console.log('coupon_rate:', isinData.coupon_rate);
      console.log('yield_rate:', isinData.yield_rate);
      console.log('maturity_date:', isinData.maturity_date);
      console.log('issue_date:', isinData.issue_date);
      console.log('coupon_date_1:', isinData.coupon_date_1);
      console.log('coupon_date_2:', isinData.coupon_date_2);
      
      // Check if we have minimum required fields for NVP calculation
      const hasRequiredFields = isinData.face_value && isinData.coupon_rate && 
                               (isinData.maturity_date || isinData.issue_date || isinData.coupon_date_1);
      
      console.log('Has required fields for NVP:', hasRequiredFields);
      
       if (hasRequiredFields) {
         // Calculate NVP for each selected deal individually using today's value date and deal's yield
         const totalSellAmount = selectedDeals.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0);
         console.log('totalSellAmount:', totalSellAmount);
         
         // Only calculate if deals are actually selected and have amounts
         if (totalSellAmount > 0 && selectedDeals.length > 0) {
           // Use formValueDate (sell value date) instead of today's date
           const systemDate = formValueDate || new Date().toISOString().split('T')[0];
           console.log('systemDate (sell value date):', systemDate);
           
           let totalNVP = 0;
           let totalSellValue = 0;
           let weightedNVPPer100 = 0;
           let dealCalculations = [];
           
           // Calculate NVP for each selected deal using its original yield (purchased yield) and sell value date
           for (const selectedDeal of selectedDeals) {
             const deal = deals.find(d => d.id === selectedDeal.dealId);
             if (deal && selectedDeal.amountToSell && Number(selectedDeal.amountToSell) > 0) {
               console.log('Calculating NVP for deal:', deal.deal_number, 'with purchased yield:', deal.yield, 'for sell value date:', systemDate);
               
               // Calculate NVP for this specific deal using its original yield rate (purchased yield) and sell value date
               const nvpParams = {
                 faceValue: Number(selectedDeal.amountToSell),
                 couponRate: isinData.coupon_rate,
                 yieldRate: deal.yield || isinData.yield_rate || 12.0, // Use deal's purchased yield
                 systemDate: systemDate, // Sell value date (formValueDate)
                 maturityDate: isinData.maturity_date,
                 issueDate: isinData.issue_date,
                 couponDate1: isinData.coupon_date_1,
                 couponDate2: isinData.coupon_date_2
               };
               
               console.log('NVP calculation parameters for deal', deal.deal_number, ':', nvpParams);
               
               const nvpResult = calculateNVP(nvpParams);
               
               if (nvpResult.nvp) {
                 const nvpPer100 = parseFloat(nvpResult.nvp);
                 const sellPriceForDeal = sellPricePer100 !== null ? sellPricePer100 : parseFloat(cleanPrice || 0);
                 const dealAmount = Number(selectedDeal.amountToSell);
                 
                 const dealNVP = (nvpPer100 / 100) * dealAmount;
                 const dealSellValue = (sellPriceForDeal / 100) * dealAmount;
                 
                 totalNVP += dealNVP;
                 totalSellValue += dealSellValue;
                 
                 // Weighted average NVP per 100
                 weightedNVPPer100 += nvpPer100 * (dealAmount / totalSellAmount);
                 
                 dealCalculations.push({
                   dealNumber: deal.deal_number,
                   yield: deal.yield,
                   amount: dealAmount,
                   nvpPer100: nvpPer100,
                   dealNVP: dealNVP,
                   dealSellValue: dealSellValue,
                   gainLoss: dealSellValue - dealNVP
                 });
                 
                 console.log(`Deal ${deal.deal_number}: NVP per 100: ${nvpPer100}, Amount: ${dealAmount}, Deal NVP: ${dealNVP}, Deal Sell Value: ${dealSellValue}`);
               }
             }
           }
           
           if (totalNVP > 0) {
             const capitalGainLoss = totalSellValue - totalNVP;
             const percentage = totalNVP > 0 ? ((capitalGainLoss / totalNVP) * 100) : 0;
             
             console.log('Aggregated calculations:');
             console.log('weightedNVPPer100:', weightedNVPPer100);
             console.log('totalNVP:', totalNVP);
             console.log('totalSellValue:', totalSellValue);
             console.log('capitalGainLoss:', capitalGainLoss);
             console.log('dealCalculations:', dealCalculations);
             
             const finalSellPrice = sellPricePer100 !== null ? sellPricePer100 : parseFloat(cleanPrice || 0);
             setGainLossData({
               nvp: weightedNVPPer100.toFixed(4),
               cleanPrice: finalSellPrice.toFixed(4),
               totalNVP: totalNVP.toFixed(2),
               totalSellValue: totalSellValue.toFixed(2),
               capitalGainLoss: capitalGainLoss.toFixed(2),
               isGain: capitalGainLoss > 0,
               percentage: percentage.toFixed(2),
               totalAmount: totalSellAmount.toFixed(2),
               dealCalculations: dealCalculations
             });
           } else {
             console.log('No valid NVP calculations for selected deals');
             setGainLossData({
               nvp: '',
               cleanPrice: '',
               totalNVP: '',
               totalSellValue: '',
               capitalGainLoss: '',
               isGain: false,
               percentage: '',
               totalAmount: '',
               dealCalculations: []
             });
           }
         } else {
           console.log('No deals selected or no amounts entered');
           setGainLossData({
             nvp: '',
             cleanPrice: '',
             totalNVP: '',
             totalSellValue: '',
             capitalGainLoss: '',
             isGain: false,
             percentage: '',
             totalAmount: '',
             dealCalculations: []
           });
         }
      } else {
        console.log('ISIN data missing required fields, using fallback calculation');
        // Use fallback calculation with available data
        const amountToUse = selectedDeals.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0);
        
        // Only calculate if deals are actually selected and have amounts
        if (amountToUse > 0 && selectedDeals.length > 0) {
          // Try to use the form's yield rate from the main page
          const formYieldRate = cleanPrice ? parseFloat(cleanPrice) : 12.0; // This might not be the yield, let's get it from the form
          
          // Use yield from ISIN data if available, otherwise use a default
          const yieldRate = isinData.yield_rate || 12.0; // Default yield
          const couponRate = isinData.coupon_rate || 7.0; // Default coupon rate
          
          console.log('Using fallback calculation with yield:', yieldRate, 'coupon:', couponRate);
          
          // Try to calculate NVP with available data, even if some fields are missing
          const systemDate = new Date().toISOString().split('T')[0];
          
          // Use default dates if missing
          const maturityDate = isinData.maturity_date || '2030-12-31';
          const issueDate = isinData.issue_date || '2020-01-01';
          const couponDate1 = isinData.coupon_date_1 || '2020-06-30';
          const couponDate2 = isinData.coupon_date_2 || '2020-12-31';
          
          const nvpResult = calculateNVP({
            faceValue: amountToUse,
            couponRate: couponRate,
            yieldRate: yieldRate,
            systemDate: systemDate,
            maturityDate: maturityDate,
            issueDate: issueDate,
            couponDate1: couponDate1,
            couponDate2: couponDate2
          });
          
          console.log('Fallback NVP result:', nvpResult);
          
          if (nvpResult.nvp) {
            const nvpPer100 = parseFloat(nvpResult.nvp);
            const sellPrice = parseFloat(cleanPrice);
            const totalNVP = (nvpPer100 / 100) * amountToUse;
            const totalSellValue = (sellPrice / 100) * amountToUse;
            const capitalGainLoss = totalSellValue - totalNVP;
            const percentage = totalNVP > 0 ? ((capitalGainLoss / totalNVP) * 100) : 0;
            
            console.log('Fallback calculation successful:', { nvpPer100, sellPrice, totalNVP, totalSellValue, capitalGainLoss });
            
            setGainLossData({
              nvp: nvpPer100.toFixed(4),
              cleanPrice: sellPrice.toFixed(4),
              totalNVP: totalNVP.toFixed(2),
              totalSellValue: totalSellValue.toFixed(2),
              capitalGainLoss: capitalGainLoss.toFixed(2),
              isGain: capitalGainLoss > 0,
              percentage: percentage.toFixed(2),
              totalAmount: amountToUse.toFixed(2)
            });
          } else {
            console.log('Fallback NVP calculation failed, using simple estimation');
            // Simple NVP estimation based on yield
            const estimatedNVP = Math.max(90, 100 - (yieldRate - 7) * 2); // Rough estimation
            const sellPrice = parseFloat(cleanPrice);
            const totalNVP = (estimatedNVP / 100) * amountToUse;
            const totalSellValue = (sellPrice / 100) * amountToUse;
            const capitalGainLoss = totalSellValue - totalNVP;
            const percentage = totalNVP > 0 ? ((capitalGainLoss / totalNVP) * 100) : 0;
            
            console.log('Simple estimation:', { estimatedNVP, sellPrice, totalNVP, totalSellValue, capitalGainLoss });
            
            setGainLossData({
              nvp: estimatedNVP.toFixed(4),
              cleanPrice: sellPrice.toFixed(4),
              totalNVP: totalNVP.toFixed(2),
              totalSellValue: totalSellValue.toFixed(2),
              capitalGainLoss: capitalGainLoss.toFixed(2),
              isGain: capitalGainLoss > 0,
              percentage: percentage.toFixed(2),
              totalAmount: amountToUse.toFixed(2)
            });
          }
        } else {
          // No deals selected, clear the data
          console.log('No deals selected or no amounts entered');
          setGainLossData({
            nvp: '',
            cleanPrice: '',
            totalNVP: '',
            totalSellValue: '',
            capitalGainLoss: '',
            isGain: false,
            percentage: '',
            totalAmount: '',
            dealCalculations: []
          });
        }
      }
    } else {
      // Even without clean price, show NVP calculation for selected deals
      const amountToUse = selectedDeals.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0);
      
      // Only calculate if deals are actually selected and have amounts
      if (amountToUse > 0 && selectedDeals.length > 0) {
        console.log('Calculating NVP without clean price for amount:', amountToUse);
        
        let totalNVP = 0;
        let weightedNVPPer100 = 0;
        let dealCalculations = [];
        
        for (const selectedDeal of selectedDeals) {
          const deal = deals.find(d => d.id === selectedDeal.dealId);
          if (deal && selectedDeal.amountToSell && Number(selectedDeal.amountToSell) > 0) {
            const dealYield = deal.yield || 12.0;
            const dealAmount = Number(selectedDeal.amountToSell);
            
            // Simple NVP estimation based on deal's yield
            const estimatedNVPPer100 = Math.max(85, 100 - (dealYield - 7) * 1.5);
            const dealNVP = (estimatedNVPPer100 / 100) * dealAmount;
            
            totalNVP += dealNVP;
            weightedNVPPer100 += estimatedNVPPer100 * (dealAmount / amountToUse);
            
            dealCalculations.push({
              dealNumber: deal.deal_number,
              yield: deal.yield,
              amount: dealAmount,
              nvpPer100: estimatedNVPPer100,
              dealNVP: dealNVP,
              dealSellValue: 0,
              gainLoss: 0
            });
            
            console.log(`Deal ${deal.deal_number}: Yield ${deal.yield}%, NVP per 100: ${estimatedNVPPer100}, Amount: ${dealAmount}`);
          }
        }
        
        if (totalNVP > 0) {
          setGainLossData({
            nvp: weightedNVPPer100.toFixed(4),
            cleanPrice: 'Enter clean price',
            totalNVP: totalNVP.toFixed(2),
            totalSellValue: '0.00',
            capitalGainLoss: '0.00',
            isGain: false,
            percentage: '0.00',
            totalAmount: amountToUse.toFixed(2),
            dealCalculations: dealCalculations
          });
        }
      } else {
        // No deals selected, clear the data
        console.log('No deals selected or no amounts entered');
        setGainLossData({
          nvp: '',
          cleanPrice: '',
          totalNVP: '',
          totalSellValue: '',
          capitalGainLoss: '',
          isGain: false,
          percentage: '',
          totalAmount: '',
          dealCalculations: []
        });
      }
    }
  }, [cleanPrice, isinData, selectedDeals]);

  useEffect(() => {
    if (!search) {
      setFilteredDeals(deals);
    } else {
      const s = search.toLowerCase();
      setFilteredDeals(
        deals.filter(d =>
          (d.deal_number && d.deal_number.toLowerCase().includes(s)) ||
          (d.yield && String(d.yield).toLowerCase().includes(s))
        )
      );
    }
  }, [search, deals]);

  const handlePortfolioChange = (portfolio) => {
    setSelectedPortfolio(portfolio);
    // Notify parent component about portfolio selection
    if (onPortfolioChange) {
      onPortfolioChange(portfolio);
    }
  };

  // Validate balance for selected deals
  const validateBalanceForDeals = (dealsToValidate) => {
    // Prepare deals data for validation
    const dealsForValidation = dealsToValidate
      .filter(deal => deal.amountToSell && Number(deal.amountToSell) > 0)
      .map(deal => {
        const dealData = deals.find(d => d.id === deal.dealId);
        return {
          ...deal,
          isin: dealData?.isin || isin, // Use deal's ISIN or fallback to modal's ISIN
          amount: deal.amountToSell
        };
      });

    if (dealsForValidation.length === 0) {
      setBalanceValidation({
        isValid: true,
        message: 'No amounts to validate',
        validationResults: []
      });
      return;
    }

    // Validate against the currently loaded buy deals for this modal/portfolio.
    // Using report-wide balances can include unrelated portfolios and show false negatives.
    const availableByIsin = {};
    (deals || []).forEach((d) => {
      const key = d?.isin || isin;
      if (!key) return;
      const remaining = Number(d?.remaining_face_value ?? d?.face_value ?? 0) || 0;
      availableByIsin[key] = (availableByIsin[key] || 0) + remaining;
    });

    const requestedByIsin = {};
    dealsForValidation.forEach((d) => {
      const key = d?.isin || isin;
      if (!key) return;
      const requested = Number(d?.amountToSell || d?.amount || 0) || 0;
      requestedByIsin[key] = (requestedByIsin[key] || 0) + requested;
    });

    const validationResults = Object.keys(requestedByIsin).map((key) => {
      const availableBalance = Number(availableByIsin[key] || 0);
      const requestedAmount = Number(requestedByIsin[key] || 0);
      const isValid = requestedAmount <= availableBalance + 0.0001;
      return {
        isin: key,
        isValid,
        message: isValid ? 'OK' : 'Insufficient available balance',
        availableBalance,
        requestedAmount,
        shortfall: isValid ? 0 : (requestedAmount - availableBalance)
      };
    });

    const hasErrors = validationResults.some((r) => !r.isValid);
    const validation = {
      isValid: !hasErrors,
      message: hasErrors ? 'Some deals exceed available balance' : 'All deals validated successfully',
      totalRequestedAmount: dealsForValidation.reduce((sum, d) => sum + (Number(d.amountToSell || d.amount || 0) || 0), 0),
      validationResults,
      hasErrors
    };
    setBalanceValidation(validation);
    
    console.log('Balance validation result:', validation);
  };

  const handleDealSelect = (dealId) => {
    setError('');
    setSelectedDeals(prev => {
      const newSelectedDeals = prev.some(d => d.dealId === dealId)
        ? prev.filter(d => d.dealId !== dealId)
        : [...prev, { dealId, amountToSell: '' }];
      
      console.log('Deal selected/deselected:', dealId, 'New selected deals:', newSelectedDeals);
      
      // Validate balance after deal selection
      validateBalanceForDeals(newSelectedDeals);
      
      return newSelectedDeals;
    });
  };

  const handleAmountChange = (dealId, value, maxValue) => {
    let v = value.replace(/[^0-9.]/g, '');
    if (Number(v) > Number(maxValue)) v = String(maxValue);
    setError('');
    setSelectedDeals(prev => {
      const newSelectedDeals = prev.map(d => d.dealId === dealId ? { ...d, amountToSell: v } : d);
      console.log('Amount changed for deal:', dealId, 'New amount:', v, 'Updated selected deals:', newSelectedDeals);
      
      // Validate balance after amount change
      validateBalanceForDeals(newSelectedDeals);
      
      return newSelectedDeals;
    });
  };

  const allocatedFaceTotal = () =>
    selectedDeals.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0);

  const faceValueMismatch = () => {
    const gsecAmount = Number(gsecFaceValue) || 0;
    const totalSellAmount = allocatedFaceTotal();
    const diff = totalSellAmount - gsecAmount;
    if (Math.abs(diff) <= 0.01) return null;
    if (diff > 0) {
      return `Allocated face value is ${diff.toLocaleString()} in excess of the sell face value (${gsecAmount.toLocaleString()}). Edit the allocation amounts.`;
    }
    return `Allocated face value is ${Math.abs(diff).toLocaleString()} less than the sell face value (${gsecAmount.toLocaleString()}). Edit the allocation amounts.`;
  };

  const handleConfirm = () => {
    // Only allow deals with a valid amount
    const valid = selectedDeals.filter(d => d.amountToSell && Number(d.amountToSell) > 0);
    if (valid.length === 0) {
      setError('Please select at least one deal and enter valid amount(s) to sell.');
      return;
    }

    // Check balance validation first
    if (!balanceValidation.isValid) {
      setError(`Balance validation failed: ${balanceValidation.message}`);
      return;
    }

    const mismatch = faceValueMismatch();
    if (mismatch) {
      if (strictFaceValueMatch) {
        setError(mismatch);
        return;
      }
      const gsecAmount = Number(gsecFaceValue) || 0;
      setAmountDifference(Math.abs(allocatedFaceTotal() - gsecAmount));
      setShowAmountWarning(true);
      return;
    }

    const confirmedDeals = valid.map(d => {
      const deal = deals.find(x => x.id === d.dealId);
      const sold = Number(d.amountToSell) || 0;
      const originalRemaining = Number(deal.remaining_face_value) || 0;
      // Truncate to 4 decimals (not round)
      const newRemaining = Math.trunc((originalRemaining - sold) * 10000) / 10000;
      return {
        ...deal,
        amountToSell: sold,
        remainingFaceValue: newRemaining.toFixed(4),
        buy_deal_number: deal.deal_number // Add reference for backend
      };
    });
    onConfirm({ deals: confirmedDeals, portfolio: selectedPortfolio });
  };

  const handleContinueWithDifference = () => {
    setShowAmountWarning(false);
    const valid = selectedDeals.filter(d => d.amountToSell && Number(d.amountToSell) > 0);
    const totalSellAmount = valid.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0);
    const confirmedDeals = valid.map(d => {
      const deal = deals.find(x => x.id === d.dealId);
      const sold = Number(d.amountToSell) || 0;
      const originalRemaining = Number(deal?.remaining_face_value) || 0;
      const newRemaining = Math.trunc((originalRemaining - sold) * 10000) / 10000;
      return {
        ...deal,
        amountToSell: sold,
        remainingFaceValue: newRemaining.toFixed(4),
        buy_deal_number: deal?.deal_number
      };
    });
    onConfirm({
      deals: confirmedDeals,
      portfolio: selectedPortfolio,
      updateFaceValue: totalSellAmount
    });
  };

  const handleAdjustAmount = () => {
    setShowAmountWarning(false);
  };



  if (!show) return null;

  return (
    <div className="gsec-sell-modal-overlay">
      <div className="gsec-sell-modal-dialog">
        <div className="modal-header">
          <h5 className="modal-title">Select Deals to Sell</h5>
          <button type="button" className="close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body gsec-sell-modal-body" style={{ display: 'flex', gap: '20px' }}>
          {/* Main content area */}
          <div style={{ flex: '2' }}>
          {error && <div className="alert alert-danger">{error}</div>}
          
          {/* Balance Validation Display */}
          {!balanceValidation.isValid && (
            <div className="alert alert-warning">
              <strong>Balance Validation Failed:</strong> {balanceValidation.message}
              {balanceValidation.validationResults.map((result, index) => (
                <div key={index} className="mt-1">
                  <small>ISIN {result.isin}: Available {result.availableBalance?.toLocaleString()}, Requested {result.requestedAmount?.toLocaleString()}</small>
                </div>
              ))}
            </div>
          )}
          
          {balanceValidation.isValid && balanceValidation.message && balanceValidation.message !== 'No amounts to validate' && (
            <div className="alert alert-success">
              <strong>Balance Validated:</strong> {balanceValidation.message}
            </div>
          )}
          
          {/* GSEC Form Face Value Display */}
          {gsecFaceValue && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '14px 20px',
                margin: '12px 0 18px',
                background: 'linear-gradient(135deg, #fff7d6 0%, #ffe79a 100%)',
                border: '2px solid #f1b500',
                borderLeft: '8px solid #f1b500',
                borderRadius: 10,
                boxShadow: '0 2px 10px rgba(241, 181, 0, 0.25)',
                animation: 'pulseHighlight 2.2s ease-in-out infinite',
              }}
              role="status"
              aria-label="GSEC Form Face Value"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: '#f1b500',
                    color: '#fff',
                    fontSize: 18,
                  }}
                  aria-hidden="true"
                >
                  <i className="fas fa-coins" />
                </span>
                <div style={{ lineHeight: 1.2 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#8a6300',
                    }}
                  >
                    GSEC Form Face Value
                  </div>
                  <div style={{ fontSize: 11, color: '#7a5800', marginTop: 2 }}>
                    Target amount to be allocated across deals below
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: '#7a4f00',
                  fontFamily: '"Segoe UI", system-ui, sans-serif',
                  letterSpacing: '0.5px',
                }}
              >
                {Number(gsecFaceValue).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <style>{`
                @keyframes pulseHighlight {
                  0%, 100% { box-shadow: 0 2px 10px rgba(241, 181, 0, 0.25); }
                  50% { box-shadow: 0 4px 18px rgba(241, 181, 0, 0.55); }
                }
              `}</style>
            </div>
          )}
          
          <div className="form-group">
            <label>Portfolio</label>
            <select className="form-control" value={selectedPortfolio} onChange={e => handlePortfolioChange(e.target.value)}>
              <option value="">Select Portfolio</option>
              {portfolios.map(p => (
                <option key={p.portfolio_id || p.id} value={p.portfolio_id || p.id}>
                  {p.portfolio_name || p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Search (Deal # or Yield)</label>
            <input className="form-control" value={search} onChange={e => setSearch(e.target.value)} placeholder="Type to filter..." />
          </div>
          {(() => {
            // Show a hint when every visible deal is ineligible because the sell value date
            // is earlier than the earliest deal's settlement date.
            if (!formValueDate || !Array.isArray(filteredDeals) || filteredDeals.length === 0) return null;
            const ineligibleDeals = filteredDeals.filter(d => d.value_date && new Date(d.value_date) > new Date(formValueDate));
            if (ineligibleDeals.length === 0 || ineligibleDeals.length !== filteredDeals.length) return null;
            const earliest = ineligibleDeals.reduce((min, d) => {
              const dv = new Date(d.value_date);
              return !min || dv < min ? dv : min;
            }, null);
            const earliestStr = earliest ? earliest.toLocaleDateString('en-GB') : '';
            const sellVdStr = (() => { try { return new Date(formValueDate).toLocaleDateString('en-GB'); } catch (_) { return formValueDate; } })();
            return (
              <div style={{
                padding: '10px 14px',
                margin: '0 0 12px',
                background: '#fff7d6',
                border: '1px solid #f1b500',
                borderLeft: '4px solid #f1b500',
                borderRadius: 6,
                color: '#7a4f00',
                fontSize: 13,
                lineHeight: 1.5,
              }}>
                <strong>None of these deals have settled yet.</strong><br />
                Your sell value date is <strong>{sellVdStr}</strong>, but the earliest deal settles on <strong>{earliestStr}</strong>.
                Close this modal, change the sell <em>Value Date</em> to <strong>{earliestStr}</strong> or later, and try again.
              </div>
            );
          })()}
          <div style={{ maxHeight: 600, overflowY: 'auto' }}>
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Deal #</th>
                  <th>ISIN</th>
                  <th>Yield</th>
                  <th>Face Value</th>
                  <th>Remaining Face Value</th>
                  <th>Value Date</th>
                  <th>Amount to Sell</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8}>Loading...</td></tr>
                ) : filteredDeals.length === 0 ? (
                  <tr><td colSpan={8}>No eligible deals found.</td></tr>
                ) : filteredDeals.map(deal => {
                  const selected = selectedDeals.some(d => d.dealId === deal.id);
                  const amountToSell = selectedDeals.find(d => d.dealId === deal.id)?.amountToSell || '';
                  // Eligibility based on value date vs form value date
                  let eligible = true;
                  let ineligibleReason = '';
                  if (formValueDate && deal.value_date) {
                    try {
                      eligible = new Date(deal.value_date) <= new Date(formValueDate);
                      if (!eligible) {
                        const dealVd = new Date(deal.value_date).toLocaleDateString('en-GB');
                        const sellVd = new Date(formValueDate).toLocaleDateString('en-GB');
                        ineligibleReason = `Settles on ${dealVd} — set sell value date to ${dealVd} or later (currently ${sellVd}).`;
                      }
                    } catch (_) {
                      eligible = true;
                    }
                  }

                  const formatVd = (v) => {
                    if (!v) return '—';
                    try { return new Date(v).toLocaleDateString('en-GB'); } catch (_) { return String(v); }
                  };

                  // Debug logging
                  console.log('Deal:', deal.id, 'Selected:', selected, 'Remaining:', deal.remaining_face_value, 'Face Value:', deal.face_value, 'Eligible:', eligible);

                  return (
                    <tr
                      key={deal.id}
                      style={!eligible ? { background: '#fff8e1', color: '#6b5b00' } : undefined}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => handleDealSelect(deal.id)}
                          disabled={!eligible}
                          title={ineligibleReason || ''}
                        />
                      </td>
                      <td>
                        {deal.deal_number}
                        {!eligible && (
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 11,
                              color: '#b45309',
                              fontStyle: 'italic',
                              maxWidth: 220,
                            }}
                            title={ineligibleReason}
                          >
                            {ineligibleReason}
                          </div>
                        )}
                      </td>
                      <td>{deal.isin}</td>
                      <td>{deal.yield}</td>
                      <td>{deal.face_value}</td>
                      <td>{deal.remaining_face_value}</td>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: !eligible ? 700 : 500 }}>
                        {formatVd(deal.value_date)}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          max={deal.remaining_face_value || deal.face_value}
                          value={amountToSell}
                          onChange={e => handleAmountChange(deal.id, e.target.value, deal.remaining_face_value || deal.face_value)}
                          disabled={!selected || !eligible}
                          className="form-control form-control-sm"
                          style={{ width: 100 }}
                          title={ineligibleReason || ''}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="7" style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</td>
                  <td style={{ fontWeight: 'bold' }}>
                    {selectedDeals && selectedDeals.length > 0
                      ? allocatedFaceTotal().toLocaleString(undefined)
                      : 0}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          </div>
          
          {/* Gain/Loss Display Sidebar */}
          <div style={{ flex: '1', minWidth: '300px' }}>
            <div className="card" style={{ position: 'sticky', top: '20px' }}>
              <div className="card-header">
                <h6 className="mb-0">Gain/Loss Analysis</h6>
              </div>
              <div className="card-body">
                {/* Debug info */}
                <div className="mb-2" style={{ fontSize: '12px', color: '#666' }}>
                  <div>Clean Price: {cleanPrice || 'Not provided'}</div>
                  <div>ISIN Data: {isinData ? 'Available' : 'Not available'}</div>
                  <div>GSEC Face Value: {gsecFaceValue || 'Not provided'}</div>
                </div>
                
                {gainLossData.nvp ? (
                  <div>
                    <div className="mb-3">
                      <small className="text-muted">Total Amount to Sell</small>
                      <div className="fw-bold">{Number(gainLossData.totalAmount).toLocaleString()}</div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Weighted NVP per 100</small>
                      <div className="fw-bold">{gainLossData.nvp}</div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Sell Price per 100</small>
                      <div className="fw-bold">{gainLossData.cleanPrice}</div>
                    </div>
                    <hr />
                    <div className="mb-3">
                      <small className="text-muted">Total NVP Value</small>
                      <div className="fw-bold">{Number(gainLossData.totalNVP).toLocaleString()}</div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Total Sell Value</small>
                      <div className="fw-bold">{Number(gainLossData.totalSellValue).toLocaleString()}</div>
                    </div>
                    <hr />
                    <div className="mb-3">
                      <small className="text-muted">Capital Gain/Loss</small>
                      <div className={`fw-bold fs-5 ${gainLossData.isGain ? 'text-success' : 'text-danger'}`}>
                        {gainLossData.isGain ? '+' : ''}{Number(gainLossData.capitalGainLoss).toLocaleString()}
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Percentage</small>
                      <div className={`fw-bold ${gainLossData.isGain ? 'text-success' : 'text-danger'}`}>
                        {gainLossData.isGain ? '+' : ''}{gainLossData.percentage}%
                      </div>
                    </div>
                    
                    {/* Per-deal breakdown */}
                    {gainLossData.dealCalculations && gainLossData.dealCalculations.length > 0 && (
                      <div className="mt-3">
                        <small className="text-muted">Per Deal Breakdown:</small>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '12px' }}>
                          {gainLossData.dealCalculations.map((deal, index) => (
                            <div key={index} className="border-bottom py-1">
                              <div><strong>{deal.dealNumber}</strong> (Yield: {deal.yield}%)</div>
                              <div>Amount: {Number(deal.amount).toLocaleString()}</div>
                              <div>NVP: {deal.nvpPer100.toFixed(4)} | Sell: {gainLossData.cleanPrice}</div>
                              <div className={deal.gainLoss > 0 ? 'text-success' : 'text-danger'}>
                                {deal.gainLoss > 0 ? '+' : ''}{Number(deal.gainLoss).toLocaleString()}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className={`alert ${gainLossData.isGain ? 'alert-success' : 'alert-danger'} mb-0 mt-2`}>
                      <strong>
                        {gainLossData.isGain ? 'CAPITAL GAIN' : 'CAPITAL LOSS'}
                      </strong>
                    </div>
                  </div>
                ) : cleanPrice ? (
                  <div className="text-warning">
                    <small>Calculating gain/loss... Check console for debug info</small>
                  </div>
                ) : (
                  <div className="text-muted">
                    <small>Enter clean price in the main form to see gain/loss analysis</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleConfirm}>Confirm</button>
        </div>
      </div>

      {showAmountWarning && !strictFaceValueMatch && (
        <div className="modal-backdrop" style={{ zIndex: 1200 }}>
          <div className="modal" style={{ display: 'flex', flexDirection: 'column', zIndex: 1300, maxWidth: 500, margin: '10% auto', maxHeight: '75vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h5 className="modal-title">Amount Mismatch Warning</h5>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning">
                <strong>Warning:</strong> There is a difference of <strong>{amountDifference.toLocaleString()}</strong> between:
                <ul className="mt-2">
                  <li>Form Face Value: <strong>{Number(gsecFaceValue).toLocaleString()}</strong></li>
                  <li>Total Sell Amount: <strong>{allocatedFaceTotal().toLocaleString()}</strong></li>
                </ul>
              </div>
              <p>Do you want to continue with the sell amount and update the form face value?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleAdjustAmount}>
                Adjust Amount
              </button>
              <button className="btn btn-warning" onClick={handleContinueWithDifference}>
                Continue & Update Form
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        .modal-backdrop { position: fixed; top:0; left:0; width:100vw; height:100vh; background:rgba(0,0,0,0.35); }
        .modal { background: #fff; border-radius: 8px; box-shadow: 0 4px 24px rgba(0,0,0,0.18); width: 100%; max-width: 1200px; min-width: 600px; }
      `}</style>
    </div>
  );
}

GsecSellDealModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onPortfolioChange: PropTypes.func,
  isin: PropTypes.string,
  gsecFaceValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cleanPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isinData: PropTypes.object,
  formValueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  strictFaceValueMatch: PropTypes.bool
};

export default GsecSellDealModal;
;